/**
 * Espace client public — Phase 3b (signature électronique).
 *
 * Route publique `/{slug}/c/quote/{token}` (aucune auth JWT — token URL suffisant).
 * Palette Wérko espace client : fond crème `#F7F3EC`, texte `#2A2420`, terracotta `#C4553B` en accent.
 * Mobile-first. Options interactives (recalcul TVAC en direct), signature canvas, checkbox CGV + attestation TVA 6% conditionnelle.
 * Envoie POST /api/public/quotes/{token}/sign avec signature PNG + options acceptées.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { formatCurrencyBE } from "@/lib/currency";
import { publicApiError } from "@/lib/api";
import { Loader2, Download, PenLine, Package, CheckCircle2, ShieldCheck, XCircle } from "lucide-react";
import PublicChantierBlock from "@/components/public/PublicChantierBlock";

const PUBLIC_REFUSE_REASONS = [
  { key: "price_too_high", label: "Prix trop élevé" },
  { key: "went_competitor", label: "J'ai choisi un autre prestataire" },
  { key: "abandoned", label: "Projet abandonné" },
  { key: "delay_too_long", label: "Délai trop long" },
  { key: "no_response", label: "Autre / je réponds plus tard" },
  { key: "other", label: "Autre (précisez)" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatDateBE(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "long", year: "numeric" });
  } catch (e) {
    return iso;
  }
}

export default function PublicQuotePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [chantierSteps, setChantierSteps] = useState(null);
  const [chantierUpdates, setChantierUpdates] = useState([]);
  const [verifyInfo, setVerifyInfo] = useState(null);
  const [error, setError] = useState(null);
  const [acceptedOpts, setAcceptedOpts] = useState({});
  const [cgvOk, setCgvOk] = useState(false);
  const [tva6Ok, setTva6Ok] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [showSign, setShowSign] = useState(false);
  const [showRefuse, setShowRefuse] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const [refuseDetail, setRefuseDetail] = useState("");
  const [refuseSubmitting, setRefuseSubmitting] = useState(false);
  const [refusedOverride, setRefusedOverride] = useState(false);
  const [signedOverride, setSignedOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Task 3 (2026-09-28) — messages inline design charte, remplacement des 3 alert()
  // natives. `signValidationError` = validation locale (signature vide) sous le pavé
  // signature. `signSubmitError` = échec réseau sur POST /sign. `refuseSubmitError`
  // = échec réseau sur POST /refuse. Tous 3 effaçables au prochain click "Signer".
  const [signValidationError, setSignValidationError] = useState("");
  const [signSubmitError, setSignSubmitError] = useState("");
  const [refuseSubmitError, setRefuseSubmitError] = useState("");
  const sigRef = useRef(null);

  const refreshChantier = async () => {
    try {
      const [stepsRes, updatesRes] = await Promise.all([
        axios.get(`${API_BASE}/api/public/chantier/${token}/steps`, { timeout: 15000 }),
        axios.get(`${API_BASE}/api/public/chantier/${token}/updates`, { timeout: 15000 }),
      ]);
      setChantierSteps(stepsRes.data);
      setChantierUpdates(updatesRes.data || []);
    } catch (e) { /* chantier pas encore visible */ }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/public/quotes/${token}`, { timeout: 20000 });
        if (!cancelled) setData(res.data);
        // Charge aussi les factures (Phase 5) — best-effort
        try {
          const inv = await axios.get(`${API_BASE}/api/public/quotes/${token}/invoices`, { timeout: 15000 });
          if (!cancelled) setInvoicesData(inv.data);
        } catch (e) { /* aucune facture ou endpoint indispo */ }
        // Phase 6 — Chantier steps + updates
        await refreshChantier();
        // Sprint polish Item 3 — verify signature payload (best-effort)
        try {
          const v = await axios.get(`${API_BASE}/api/public/quotes/${token}/verify`, { timeout: 10000 });
          if (!cancelled) setVerifyInfo(v.data);
        } catch (e) { /* pas encore signé */ }
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Dérivés — safe même si data est null grâce aux fallbacks
  const quote = data?.quote || {};
  const co = data?.company_snapshot || {};
  const status = quote.status || "envoye";
  const accent = co.accent_color || "#C4553B";
  const optionLines = useMemo(() => (quote.lines || []).filter((l) => l.is_option), [quote.lines]);
  const hasTva6 = useMemo(
    () => (quote.lines || []).some((l) => l.vat_rate === 6 && !l.is_note && !l.is_option),
    [quote.lines],
  );
  const signed = signedOverride || status === "signe";
  const refused = refusedOverride || status === "refuse";

  const previewTotals = useMemo(() => {
    let subtotal = quote.subtotal_htva || 0;
    let tvac = quote.total_tvac || 0;
    let extraHt = 0;
    let extraTvac = 0;
    for (const l of optionLines) {
      if (acceptedOpts[l.id]) {
        const ht = (l.quantity || 0) * (l.unit_price || 0);
        extraHt += ht;
        extraTvac += ht * (1 + l.vat_rate / 100);
      }
    }
    return {
      subtotal_htva: subtotal + extraHt,
      total_tvac: tvac + extraTvac,
    };
  }, [acceptedOpts, optionLines, quote.subtotal_htva, quote.total_tvac]);

  const sign = async () => {
    // Task 3 (2026-09-28) — reset des 2 messages inline avant chaque tentative.
    setSignValidationError("");
    setSignSubmitError("");
    if (!sigRef.current || sigRef.current.isEmpty()) {
      // Validation locale — message inline sous le pavé signature, PAS alert().
      setSignValidationError("Merci de dessiner votre signature avant de valider.");
      return;
    }
    setSubmitting(true);
    try {
      // Task 3 (2026-09-28, révision 2026-09-30) — Envoi CGV : si tenant sans
      // cgv_pdf_url, la case n'est pas affichée → on envoie `false` (cgvOk state
      // initial). Le backend acceptera car il vérifie la case seulement si un
      // PDF CGV est configuré côté tenant (à valider par test intégration
      // /api/public/quotes/{token}/sign avec devis d'un tenant sans CGV).
      const png = sigRef.current.toDataURL("image/png").split(",")[1];
      const res = await axios.post(
        `${API_BASE}/api/public/quotes/${token}/sign`,
        {
          signature_png_base64: png,
          signer_name: signerName.trim(),
          cgv_accepted: cgvOk,
          vat_6_attestation: hasTva6 ? tva6Ok : null,
          options_accepted: Object.entries(acceptedOpts).filter(([, v]) => v).map(([k]) => k),
        },
        { timeout: 30000 },
      );
      if (res.data.ok) setSignedOverride(true);
    } catch (e) {
      // Commit 2 hardening (2026-09-21) + Task 3 (2026-09-28) — Message métier
      // inline, jamais d'alert() native ni de détail backend à l'écran.
      const contact = co.name ? `contactez ${co.name}` : "contactez votre entreprise";
      setSignSubmitError(
        publicApiError(
          e,
          `La signature n'a pas pu être enregistrée. Réessayez dans un instant. Si le problème persiste, ${contact}.`,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ------ Early returns AFTER all hooks ------
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F7F3EC", color: "#2A2420" }}>
        <div className="max-w-md px-6 py-10 text-center" data-testid="public-quote-error">
          <h1 className="mb-2 text-2xl font-bold">Lien invalide ou expiré</h1>
          <p className="text-sm opacity-70">
            Ce lien n&apos;est plus valide. Contactez votre interlocuteur pour en recevoir un nouveau.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F7F3EC" }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#C4553B" }} />
      </div>
    );
  }

  const now = Date.now();
  const expiresAt = quote.expires_at ? new Date(quote.expires_at).getTime() : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 86400000)) : null;
  const isExpired = status === "expire" || (expiresAt && expiresAt < now && status === "envoye");
  const canSign = status === "envoye" && !isExpired && !signed && !refused;
  const canRefuse = status === "envoye" && !isExpired && !signed && !refused;

  const submitRefuse = async () => {
    if (!refuseReason) return;
    if (refuseReason === "other" && !refuseDetail.trim()) return;
    setRefuseSubmitting(true);
    setRefuseSubmitError("");
    try {
      const res = await axios.post(
        `${API_BASE}/api/public/quotes/${token}/refuse`,
        { reason: refuseReason, detail: refuseDetail.trim() || null },
        { timeout: 15000 },
      );
      if (res.data?.ok) {
        setRefusedOverride(true);
        setShowRefuse(false);
      }
    } catch (e) {
      // Task 3 (2026-09-28) — message inline design charte, remplacement alert().
      const contact = co.name ? `contactez ${co.name}` : "contactez votre entreprise";
      setRefuseSubmitError(
        publicApiError(
          e,
          `Impossible d'enregistrer votre choix. Réessayez dans un instant. Si le problème persiste, ${contact}.`,
        ),
      );
    } finally {
      setRefuseSubmitting(false);
    }
  };

  const banner = isExpired
    ? { bg: "#8B2E1F", text: "#F7F3EC", label: `Ce devis a expiré le ${formatDateBE(quote.expires_at)} — contactez votre interlocuteur` }
    : status === "signe"
      ? { bg: "#5A6338", text: "#F7F3EC", label: `Signé le ${formatDateBE(quote.signed_at)}` }
      : status === "envoye"
        ? { bg: "#A8751C", text: "#F7F3EC", label: `Votre devis vous attend — ${daysLeft} jour${daysLeft > 1 ? "s" : ""} pour signer` }
        : { bg: "#5A5148", text: "#F7F3EC", label: "Devis" };

  if (signed) {
    return (
      <div style={{ background: "#F7F3EC", color: "#2A2420", minHeight: "100vh" }} data-testid="public-quote-signed">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
          {/* Banner de confirmation */}
          <div className="mb-6 rounded-lg p-6 text-center" style={{ background: "#FFFFFF", border: `2px solid #5A6338` }}>
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12" style={{ color: "#5A6338" }} />
            <h1 className="mb-1 text-xl font-bold">Merci, votre devis est signé</h1>
            {quote.signed_at && (
              <p className="mb-3 text-sm font-semibold" style={{ color: "#5A6338" }} data-testid="signed-date">
                Signé le {formatDateBE(quote.signed_at)}
              </p>
            )}
            <a
              href={`${API_BASE}/api/public/quotes/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
              style={{ background: accent, color: "#F7F3EC" }}
              data-testid="signed-download-pdf"
            >
              <Download className="h-4 w-4" /> Télécharger le devis signé
            </a>
          </div>

          {/* Sprint polish Item 3 — Bloc vérification de la signature (empreinte anti-fraude) */}
          {verifyInfo?.signed && verifyInfo?.signature_pdf_hash_short && (
            <div
              className="mb-6 rounded-lg p-4 text-sm"
              style={{ background: "#FFFFFF", border: "1px solid #E5DDD1" }}
              data-testid="public-quote-verify-block"
            >
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "#5A6338" }} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold" style={{ color: "#2A2420" }}>Signature vérifiable</div>
                  <p className="mt-1 text-xs" style={{ color: "#5B5148" }}>
                    Ce devis a été signé électroniquement le{" "}
                    <b>{formatDateBE(verifyInfo.signed_at)}</b> par{" "}
                    <b>{verifyInfo.signer_name}</b>. L&apos;empreinte cryptographique du PDF signé
                    (SHA-256) permet de prouver qu&apos;aucune modification n&apos;a été apportée depuis.
                  </p>
                  <div className="mt-2 rounded p-2 font-mono text-[11px] break-all" style={{ background: "#FAF7F1", color: "#5B5148" }}
                       data-testid="verify-hash-short">
                    {verifyInfo.signature_pdf_hash_short}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Factures — visible après signature */}
          {invoicesData?.invoices?.length > 0 && (
            <section
              className="rounded-lg p-4"
              style={{ background: "#FFFFFF", border: `1px solid #E5DDD1` }}
              data-testid="public-invoices-section"
            >
              <h2 className="mb-3 text-lg font-semibold" style={{ color: accent }}>Vos factures</h2>
              {invoicesData.company_iban && (
                <div className="mb-3 rounded p-3 text-sm" style={{ background: "#FEF9F1", borderLeft: `3px solid ${accent}` }}>
                  <div><span className="opacity-70">Virement à l&apos;ordre de</span> <b>IBAN {invoicesData.company_iban}</b>{invoicesData.company_bic && ` · BIC ${invoicesData.company_bic}`}</div>
                  <div className="mt-1 text-xs opacity-70">Merci d&apos;indiquer la <b>communication structurée</b> ci-dessous à l&apos;identique.</div>
                </div>
              )}
              <ul className="space-y-2">
                {invoicesData.invoices.map((inv) => (
                  <li
                    key={inv.id}
                    className="rounded border p-3"
                    style={{ borderColor: inv.status === "paid" ? "#7A8450" : inv.status === "overdue" ? "#8B2E1F" : "#E5DDD1", background: inv.status === "paid" ? "#F4F8EC" : "#FFFFFF" }}
                    data-testid={`public-invoice-${inv.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{inv.number}</span>
                          <span
                            className="rounded-full px-2 py-0.5 text-xs"
                            style={inv.status === "paid"
                              ? { background: "#7A8450", color: "#F7F3EC" }
                              : inv.status === "overdue"
                                ? { background: "#8B2E1F", color: "#F7F3EC" }
                                : { background: "#A8751C", color: "#F7F3EC" }}
                          >
                            {inv.type === "acompte" ? "Acompte" : "Solde"} ·{" "}
                            {inv.status === "paid" ? "Payée" : inv.status === "overdue" ? "En retard" : "En attente"}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs opacity-70">
                          Émise le {formatDateBE(inv.issued_at)} · Échéance {formatDateBE(inv.due_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="tabular text-lg font-semibold">{formatCurrencyBE(inv.amount_tvac)}</div>
                      </div>
                    </div>
                    {inv.status !== "paid" && (
                      <div className="mt-2 rounded p-2 font-mono text-sm font-semibold" style={{ background: "#F7F3EC", color: "#2A2420" }} data-testid={`public-invoice-comm-${inv.id}`}>
                        {inv.structured_communication}
                      </div>
                    )}
                    <div className="mt-2">
                      {inv.has_pdf && (
                        <a
                          href={`${API_BASE}/api/public/quotes/${token}/invoices/${inv.id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm hover:underline"
                          style={{ color: accent }}
                          data-testid={`public-invoice-pdf-${inv.id}`}
                        >
                          <Download className="h-3 w-3" /> Télécharger le PDF
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Phase 6 — Suivi de chantier (post-signature) */}
          <PublicChantierBlock
            token={token}
            accent={accent}
            chantierSteps={chantierSteps}
            chantierUpdates={chantierUpdates}
            chantier={chantierSteps}
            onRecapSigned={refreshChantier}
          companyName={co.name}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ background: "#F7F3EC", color: "#2A2420", minHeight: "100vh" }}
      data-testid="public-quote-page"
    >
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        {/* Header */}
        <header className="mb-6 flex items-center justify-between border-b pb-4" style={{ borderColor: "#E5DDD1" }}>
          <div className="flex items-center gap-3">
            {(co.logo_file_id || co.logo_url) ? (
              <img
                src={co.logo_file_id
                  ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/logos/${co.logo_file_id}`
                  : co.logo_url}
                alt={co.name}
                className="h-10 max-w-[140px]"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <span className="text-lg font-semibold">{co.name}</span>
            )}
          </div>
          <div className="text-right text-xs opacity-70">
            Espace client<br />
            <span style={{ color: accent }}>wérko</span>
          </div>
        </header>

        {/* Titre */}
        <h1 className="text-2xl font-bold sm:text-3xl" data-testid="public-quote-title">
          Votre devis <span className="font-mono">{quote.number || "—"}</span>
        </h1>
        {quote.title && <p className="mt-1 text-lg opacity-80">{quote.title}</p>}

        {/* Bandeau statut */}
        <div
          className="mt-4 rounded-lg px-4 py-3 text-sm font-semibold"
          style={{ background: banner.bg, color: banner.text }}
          data-testid="public-quote-status-banner"
        >
          {banner.label}
        </div>

        {/* Bloc entreprise */}
        <section className="mt-6 rounded-lg border p-4" style={{ borderColor: "#E5DDD1", background: "#FDFAF3" }}>
          <div className="text-xs uppercase tracking-wider opacity-70 mb-1">Émis par</div>
          <div className="font-semibold">{co.name}</div>
          {co.address && <div className="text-sm opacity-80">{co.address}</div>}
          {co.phone && <div className="text-sm opacity-80">Tél : {co.phone}</div>}
          {co.contact_email && <div className="text-sm opacity-80">Email : {co.contact_email}</div>}
          {co.website && <div className="text-sm opacity-80">Site : {co.website}</div>}
        </section>

        {/* Résumé lignes */}
        <section className="mt-6" data-testid="public-quote-lines">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider opacity-70">Détail</h2>
          <div className="overflow-hidden rounded-lg border" style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "#F5EFE7" }}>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {(quote.lines || []).map((l, i) => (
                  <tr
                    key={l.id || i}
                    style={{ borderTop: "1px solid #F0E9DC", background: l.is_option ? "#FBF3E4" : "transparent" }}
                    data-testid={`public-quote-line-${i}`}
                  >
                    <td className="px-3 py-2">
                      {l.is_option && (
                        <span
                          className="mr-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                          style={{ background: "#D99A2B", color: "#FDFAF3" }}
                        >
                          <Package className="h-3 w-3" /> Option
                        </span>
                      )}
                      {l.is_note ? <em className="opacity-70">{l.description}</em> : l.description}
                      {!l.is_note && (
                        <span className="ml-2 text-xs opacity-50">
                          {l.quantity} {l.unit} · TVA {l.vat_rate}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular">
                      {l.is_note ? "—" : (l.is_option ? `(${formatCurrencyBE((l.quantity || 0) * (l.unit_price || 0))})` : formatCurrencyBE((l.quantity || 0) * (l.unit_price || 0)))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {optionLines.length > 0 && (
            <p className="mt-2 text-xs opacity-70">
              Les options sont exclues du total ci-dessous. Vous pourrez choisir vos options avant de signer.
            </p>
          )}
        </section>

        {/* Total */}
        <section className="mt-6 rounded-lg p-4" style={{ background: "#FFFFFF", border: `1px solid #E5DDD1` }}>
          <div className="flex items-baseline justify-between">
            <span className="text-sm opacity-70">Sous-total HTVA</span>
            <span className="tabular font-semibold">{formatCurrencyBE(quote.subtotal_htva || 0)}</span>
          </div>
          {Object.entries(quote.total_vat_by_rate || {}).map(([rate, v]) => (
            <div key={rate} className="mt-1 flex items-baseline justify-between text-sm">
              <span className="opacity-70">TVA {rate}%</span>
              <span className="tabular">{formatCurrencyBE(v)}</span>
            </div>
          ))}
          <div className="mt-3 flex items-baseline justify-between border-t pt-3" style={{ borderColor: "#E5DDD1" }}>
            <span className="text-base font-semibold">Total TVAC</span>
            <span className="tabular text-2xl font-bold" style={{ color: accent }} data-testid="public-quote-total-tvac">
              {formatCurrencyBE(quote.total_tvac || 0)}
            </span>
          </div>
        </section>

        {/* Factures — Phase 5 */}
        {invoicesData?.invoices?.length > 0 && (
          <section
            className="mt-6 rounded-lg p-4"
            style={{ background: "#FFFFFF", border: `1px solid #E5DDD1` }}
            data-testid="public-invoices-section"
          >
            <h2 className="mb-3 text-lg font-semibold" style={{ color: accent }}>Vos factures</h2>
            {invoicesData.company_iban && (
              <div className="mb-3 rounded p-3 text-sm" style={{ background: "#FEF9F1", borderLeft: `3px solid ${accent}` }}>
                <div><span className="opacity-70">Virement à l&apos;ordre de</span> <b>IBAN {invoicesData.company_iban}</b>{invoicesData.company_bic && ` · BIC ${invoicesData.company_bic}`}</div>
                <div className="mt-1 text-xs opacity-70">Merci d&apos;indiquer la <b>communication structurée</b> ci-dessous à l&apos;identique.</div>
              </div>
            )}
            <ul className="space-y-2">
              {invoicesData.invoices.map((inv) => (
                <li
                  key={inv.id}
                  className="rounded border p-3"
                  style={{ borderColor: inv.status === "paid" ? "#7A8450" : inv.status === "overdue" ? "#8B2E1F" : "#E5DDD1", background: inv.status === "paid" ? "#F4F8EC" : "#FFFFFF" }}
                  data-testid={`public-invoice-${inv.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold">{inv.number}</span>
                        <span
                          className="rounded-full px-2 py-0.5 text-xs"
                          style={inv.status === "paid"
                            ? { background: "#7A8450", color: "#F7F3EC" }
                            : inv.status === "overdue"
                              ? { background: "#8B2E1F", color: "#F7F3EC" }
                              : { background: "#A8751C", color: "#F7F3EC" }}
                        >
                          {inv.type === "acompte" ? "Acompte" : "Solde"} ·{" "}
                          {inv.status === "paid" ? "Payée" : inv.status === "overdue" ? "En retard" : "En attente"}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs opacity-70">
                        Émise le {formatDateBE(inv.issued_at)} · Échéance {formatDateBE(inv.due_at)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tabular text-lg font-semibold">{formatCurrencyBE(inv.amount_tvac)}</div>
                    </div>
                  </div>
                  {inv.status !== "paid" && (
                    <div className="mt-2 rounded p-2 font-mono text-sm font-semibold" style={{ background: "#F7F3EC", color: "#2A2420" }} data-testid={`public-invoice-comm-${inv.id}`}>
                      {inv.structured_communication}
                    </div>
                  )}
                  <div className="mt-2">
                    {inv.has_pdf && (
                      <a
                        href={`${API_BASE}/api/public/quotes/${token}/invoices/${inv.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm hover:underline"
                        style={{ color: accent }}
                        data-testid={`public-invoice-pdf-${inv.id}`}
                      >
                        <Download className="h-3 w-3" /> Télécharger le PDF
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <a
            href={`${API_BASE}/api/public/quotes/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold"
            style={{ background: accent, color: "#F7F3EC" }}
            data-testid="public-quote-download-pdf"
          >
            <Download className="h-4 w-4" /> Télécharger le PDF
          </a>
          <button
            disabled={!canSign}
            onClick={() => setShowSign(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={canSign ? { background: accent, color: "#F7F3EC", borderColor: accent } : { borderColor: "#E5DDD1", color: "#2A2420" }}
            data-testid="public-quote-sign-btn"
          >
            <PenLine className="h-4 w-4" /> {canSign ? "Signer le devis" : "Signature indisponible"}
          </button>
          {canRefuse && (
            <button
              onClick={() => setShowRefuse(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#8B2E1F] px-5 py-3 text-sm font-semibold text-[#8B2E1F] hover:bg-[#8B2E1F]/5"
              data-testid="public-quote-refuse-btn"
            >
              <XCircle className="h-4 w-4" /> Refuser
            </button>
          )}
          {refused && (
            <span
              className="inline-flex items-center gap-2 rounded-lg border border-[#8B2E1F] bg-[#8B2E1F]/10 px-4 py-3 text-sm font-semibold text-[#8B2E1F]"
              data-testid="public-quote-refused-banner"
            >
              <XCircle className="h-4 w-4" /> Devis refusé
            </span>
          )}
        </div>

        {showRefuse && canRefuse && (
          <div className="mt-6 rounded-lg border p-4 sm:p-6" style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }} data-testid="public-refuse-block">
            <h2 className="mb-3 text-lg font-semibold" style={{ color: "#2A2420" }}>Pourquoi refusez-vous ce devis ?</h2>
            <p className="mb-4 text-sm" style={{ color: "#5B5148" }}>
              Votre retour reste confidentiel et aide {quote.company_snapshot?.name || "l'entreprise"} à améliorer ses offres.
            </p>
            <div className="space-y-2">
              {PUBLIC_REFUSE_REASONS.map((r) => (
                <label
                  key={r.key}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm ${
                    refuseReason === r.key ? "border-[#8B2E1F] bg-[#8B2E1F]/5" : "border-[#E5DDD1]"
                  }`}
                  data-testid={`public-refuse-reason-${r.key}`}
                >
                  <input
                    type="radio" name="refuse_reason" value={r.key}
                    checked={refuseReason === r.key}
                    onChange={() => setRefuseReason(r.key)}
                    className="accent-[#8B2E1F]"
                  />
                  <span style={{ color: "#2A2420" }}>{r.label}</span>
                </label>
              ))}
            </div>
            {refuseReason === "other" && (
              <textarea
                value={refuseDetail}
                onChange={(e) => setRefuseDetail(e.target.value.slice(0, 500))}
                placeholder="Précisez (500 caractères max)"
                maxLength={500}
                className="mt-3 w-full rounded-md border border-[#E5DDD1] p-2 text-sm"
                data-testid="public-refuse-detail"
                rows={3}
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowRefuse(false)}
                disabled={refuseSubmitting}
                className="rounded-md border border-[#E5DDD1] px-4 py-2 text-sm text-[#2A2420]"
                data-testid="public-refuse-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submitRefuse}
                disabled={refuseSubmitting || !refuseReason || (refuseReason === "other" && !refuseDetail.trim())}
                className="rounded-md bg-[#8B2E1F] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="public-refuse-submit"
              >
                {refuseSubmitting ? "Envoi…" : "Confirmer le refus"}
              </button>
            </div>
            {/* Task 3 (2026-09-28) — Message inline design charte remplaçant alert(). */}
            {refuseSubmitError && (
              <div
                role="alert"
                className="mt-3 rounded-md border px-3 py-2 text-sm flex items-start justify-between gap-3"
                style={{ borderColor: "#8B2E1F", background: "#F9E9E4", color: "#8B2E1F" }}
                data-testid="refuse-submit-error"
              >
                <span>{refuseSubmitError}</span>
                <button
                  onClick={submitRefuse}
                  disabled={refuseSubmitting}
                  className="shrink-0 rounded border px-2 py-0.5 text-xs font-semibold disabled:opacity-50"
                  style={{ borderColor: "#8B2E1F", color: "#8B2E1F", background: "#FFFFFF" }}
                  data-testid="refuse-submit-retry"
                >
                  Réessayer
                </button>
              </div>
            )}
          </div>
        )}

        {showSign && canSign && (
          <div className="mt-6 rounded-lg border p-4 sm:p-6" style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }} data-testid="signature-block">
            <h2 className="mb-4 text-lg font-semibold">Signer votre devis</h2>

            {optionLines.length > 0 && (
              <fieldset className="mb-4">
                <legend className="mb-2 text-sm font-semibold">Vos options</legend>
                {optionLines.map((l) => (
                  <label key={l.id} className="mb-1 flex items-center gap-2 text-sm" data-testid={`opt-${l.id}`}>
                    <input
                      type="checkbox"
                      checked={!!acceptedOpts[l.id]}
                      onChange={(e) => setAcceptedOpts({ ...acceptedOpts, [l.id]: e.target.checked })}
                    />
                    <span className="flex-1">{l.description}</span>
                    <span className="tabular font-semibold">+{formatCurrencyBE((l.quantity || 0) * (l.unit_price || 0))}</span>
                  </label>
                ))}
                <div className="mt-2 rounded bg-[#FDFAF3] p-2 text-xs">
                  <div className="flex justify-between"><span>Sous-total HTVA</span><span className="tabular">{formatCurrencyBE(previewTotals.subtotal_htva)}</span></div>
                  <div className="flex justify-between font-bold" style={{ color: accent }}>
                    <span>TVAC final</span><span className="tabular" data-testid="sign-preview-tvac">{formatCurrencyBE(previewTotals.total_tvac)}</span>
                  </div>
                </div>
              </fieldset>
            )}

            <div className="mb-3">
              <label className="block text-sm font-semibold mb-1">Nom complet du signataire *</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="w-full rounded border px-3 py-2 text-sm"
                style={{ borderColor: "#E5DDD1", background: "#FDFAF3", color: "#2A2420" }}
                data-testid="signer-name-input"
              />
            </div>

            {/* Task 3 (2026-09-28, révision 2026-09-30) — Case CGV conditionnelle
                à la présence de `co.cgv_pdf_url`. Si le tenant n'a pas configuré
                de PDF CGV, la case n'est PAS affichée : faire cocher un client
                qu'il a lu un document inexistant = attestation fausse. Case
                JAMAIS pré-cochée (cgvOk = useState(false) ligne 47), consentement
                pré-coché non opposable en droit belge. */}
            {co.cgv_pdf_url && (
              <label className="mb-2 flex items-start gap-2 text-sm" data-testid="cgv-checkbox">
                <input
                  type="checkbox"
                  checked={cgvOk}
                  onChange={(e) => setCgvOk(e.target.checked)}
                  className="mt-0.5"
                  data-testid="cgv-checkbox-input"
                />
                <span>
                  J&apos;ai lu et j&apos;accepte les{" "}
                  <a
                    href={co.cgv_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: accent }}
                    data-testid="cgv-link"
                  >
                    Conditions Générales de Vente
                  </a>
                  {" *"}
                </span>
              </label>
            )}

            {hasTva6 && (
              <label className="mb-2 flex items-start gap-2 text-sm" data-testid="tva6-checkbox">
                <input type="checkbox" checked={tva6Ok} onChange={(e) => setTva6Ok(e.target.checked)} className="mt-0.5" />
                <span>J&apos;atteste que le bâtiment remplit les conditions du taux réduit TVA 6% (habitation privée &gt;10 ans) *</span>
              </label>
            )}

            <div className="mt-3 mb-3">
              <div className="mb-1 text-sm font-semibold">Signature *</div>
              <div className="rounded border" style={{ borderColor: "#E5DDD1", background: "#FDFAF3" }}>
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#2A2420"
                  canvasProps={{ className: "w-full", height: 160, "data-testid": "signature-canvas" }}
                />
              </div>
              <button
                onClick={() => sigRef.current?.clear()}
                className="mt-1 text-xs underline opacity-70"
                data-testid="signature-clear"
              >
                Effacer
              </button>
            </div>

            {/* Task 3 (2026-09-28) — Messages inline design charte remplaçant alert().
                Validation locale (signature vide) et échec réseau, sous le pavé signature. */}
            {signValidationError && (
              <div
                role="alert"
                className="mb-3 rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: "#8B2E1F", background: "#F9E9E4", color: "#8B2E1F" }}
                data-testid="sign-validation-error"
              >
                {signValidationError}
              </div>
            )}
            {signSubmitError && (
              <div
                role="alert"
                className="mb-3 rounded-md border px-3 py-2 text-sm flex items-start justify-between gap-3"
                style={{ borderColor: "#8B2E1F", background: "#F9E9E4", color: "#8B2E1F" }}
                data-testid="sign-submit-error"
              >
                <span>{signSubmitError}</span>
                <button
                  onClick={sign}
                  disabled={submitting}
                  className="shrink-0 rounded border px-2 py-0.5 text-xs font-semibold disabled:opacity-50"
                  style={{ borderColor: "#8B2E1F", color: "#8B2E1F", background: "#FFFFFF" }}
                  data-testid="sign-submit-retry"
                >
                  Réessayer
                </button>
              </div>
            )}

            <button
              onClick={sign}
              disabled={submitting || (co.cgv_pdf_url && !cgvOk) || (hasTva6 && !tva6Ok) || !signerName.trim() || signerName.length < 2}
              className="mt-2 w-full rounded-lg py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: accent, color: "#F7F3EC" }}
              data-testid="signature-submit"
            >
              {submitting ? "Signature en cours…" : "Signer et valider le devis"}
            </button>
          </div>
        )}

        {/* Phase 6 — Suivi de chantier (visible dès qu'un chantier a démarré) */}
        <PublicChantierBlock
          token={token}
          accent={accent}
          chantierSteps={chantierSteps}
          chantierUpdates={chantierUpdates}
          chantier={chantierSteps}
          onRecapSigned={refreshChantier}
          companyName={co.name}
        />

        {/* Footer */}
        <footer className="mt-10 border-t pt-4 text-center text-xs opacity-60" style={{ borderColor: "#E5DDD1" }}>
          Cet espace est sécurisé et personnel. Émis via <span style={{ color: accent }}>wérko</span>.
        </footer>
      </div>
    </div>
  );
}
